/**
 * 3D diameter label overlay with leader line from contour to offset label.
 *
 * Places the label on the opposite side of the canvas from the contour centroid,
 * draws a dashed leader line from the closest contour point, and renders the
 * text above a horizontal underline.
 */

import type { Vec3 } from '../../domain';
import type { IViewport } from '../../viewports';
import type { IOverlayObject } from '../types';

export interface DiameterLabelOverlayConfig {
    readonly id: string;
    readonly measurementName: string;
    readonly value: number;
    readonly contour: ReadonlyArray<Vec3>;
    readonly color?: string;
    readonly labelOffset?: number;
}

export class DiameterLabelOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 250;
    readonly interactive = false;
    readonly clipByCamera = false;
    visible = true;

    private measurementName: string;
    private value: number;
    private contour: ReadonlyArray<Vec3>;
    private color: string;
    private labelOffset: number;

    private readonly font = "14px 'JetBrains Mono', monospace";
    private readonly lineWidth = 1;

    constructor(config: DiameterLabelOverlayConfig) {
        this.id = config.id;
        this.measurementName = config.measurementName;
        this.value = config.value;
        this.contour = config.contour;
        this.color = config.color ?? 'rgb(102,204,255)';
        this.labelOffset = config.labelOffset ?? 80;
    }

    setContour(contour: ReadonlyArray<Vec3>): void {
        this.contour = contour;
    }

    setValue(value: number): void {
        this.value = value;
    }

    setMeasurementName(name: string): void {
        this.measurementName = name;
    }

    hitTest(_canvasX: number, _canvasY: number): boolean {
        return false;
    }

    onCameraChanged(_viewport: IViewport): void {
        // Canvas positions are updated in render()
    }

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        if (this.contour.length === 0) return;

        const overlayLayer = viewport.getOverlayLayer();
        const canvasWidth = overlayLayer.getCssWidth();

        // Calculate contour centroid in canvas space
        const centroid = this.calculateCentroid(this.contour);
        const centroidProj = viewport.worldToCanvas(centroid);
        if (!centroidProj.visible) return;

        const centroidX = centroidProj.x;
        const centroidY = centroidProj.y;

        // Place label on opposite side from centroid
        const labelOnRight = centroidX > canvasWidth / 2;

        // Format label text
        const valueText = `${this.value.toFixed(1)} mm`;
        const legend = this.measurementName ? `${this.measurementName}: ${valueText}` : valueText;

        // Measure text
        ctx.font = this.font;
        const metrics = ctx.measureText(legend);
        const textWidth = metrics.width;

        // Calculate label position (offset from centroid)
        const labelX = labelOnRight ? centroidX + this.labelOffset : centroidX - this.labelOffset - textWidth;
        const labelY = centroidY;

        // Find closest contour point to label anchor
        const anchorX = labelOnRight ? labelX : labelX + textWidth;
        const closest = this.findClosestContourPoint(viewport, anchorX, labelY);
        if (!closest) return;

        ctx.save();

        ctx.strokeStyle = this.color;
        ctx.fillStyle = this.color;
        ctx.lineWidth = this.lineWidth;

        // Dashed leader line from contour to label anchor
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(closest.x, closest.y);
        ctx.lineTo(anchorX, labelY);
        ctx.stroke();

        // Solid horizontal underline
        ctx.setLineDash([]);
        const underlineEndX = labelOnRight ? labelX + textWidth : labelX;
        ctx.beginPath();
        ctx.moveTo(anchorX, labelY);
        ctx.lineTo(underlineEndX, labelY);
        ctx.stroke();

        // Text above the underline
        ctx.textAlign = labelOnRight ? 'left' : 'right';
        ctx.textBaseline = 'bottom';
        const textX = labelOnRight ? labelX : labelX + textWidth;

        // Shadow for visibility
        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        ctx.fillText(legend, textX, labelY - 2);

        ctx.restore();
    }

    private calculateCentroid(contour: ReadonlyArray<Vec3>): Vec3 {
        let cx = 0;
        let cy = 0;
        let cz = 0;
        for (const p of contour) {
            cx += p[0];
            cy += p[1];
            cz += p[2];
        }
        const n = contour.length;
        return [cx / n, cy / n, cz / n];
    }

    private findClosestContourPoint(
        viewport: IViewport,
        targetX: number,
        targetY: number
    ): { x: number; y: number } | null {
        let closestDist = Infinity;
        let closestPoint: { x: number; y: number } | null = null;

        for (const worldPt of this.contour) {
            const proj = viewport.worldToCanvas(worldPt);
            if (!proj.visible) continue;

            const dx = proj.x - targetX;
            const dy = proj.y - targetY;
            const dist = dx * dx + dy * dy;

            if (dist < closestDist) {
                closestDist = dist;
                closestPoint = { x: proj.x, y: proj.y };
            }
        }

        return closestPoint;
    }
}
